import { z } from "zod";

/* ===========================================================================
 * POST /v1/funds/:id/collection-schedules — Create Periodic Collection
 * (US-E6.F1-0035 / @UC0038, Phase 7 Collections — @gate money,rbac).
 *
 * Cross-check index §1/§6 (PO chốt): tạo CollectionSchedule (recurrence + scheduleStatus) RỒI sinh
 * đợt thu (Collection kind=periodic) ĐẦU TIÊN đồng bộ + member_dues `pending` cho mọi member ACTIVE.
 * Recurring auto-gen DEFER P8.5 worker. KHÔNG ghi ledger (member_dues ở `pending`).
 *
 * Một nguồn sự thật cho api (validate biên + shape response) và mobile (gọi + map) — DRY.
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - B1 tiền BigInt: `amountPerMemberMinor` truyền dạng STRING số nguyên (chống mất chính xác JSON
 *    number > 2^53). Server parse → BigInt. KHÔNG float ở bất kỳ tầng nào.
 *  - C5 `.strict()` chặn over-post (KHÔNG nhận currency/fundId/scheduleStatus/id từ body — currency
 *    kế thừa fund, fundId từ :id param, status do server set 'active', id server sinh).
 *  - C4 RBAC OWNER/ADMIN/TREASURER (@RequireFundRoles trên FundAccessGuard); Member → 403;
 *    non-member/cross-tenant/non-UUID → 404 đồng nhất.
 *  - Validate biên: số tiền > 0 (AC4), ngày bắt đầu không quá khứ (AC2 — kiểm tz ở service/domain),
 *    weekly bắt buộc weekday 1..7 (AC3).
 * ========================================================================= */

/** Tần suất thu định kỳ (khớp domain COLLECTION_FREQUENCIES). */
export const COLLECTION_FREQUENCY = {
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  YEARLY: "yearly",
  /**
   * US-E23.F3-0333-S07 (QĐ PO-3 29/07) — năm học: bỏ qua tháng hè, mốc tham số hoá ở
   * `system_settings`. XỨNG ĐÁNG enum riêng vì là ĐƠN VỊ lặp có **khoảng trống** — không diễn đạt
   * được bằng `{monthly, interval:N}` (interval đều đặn không mô tả nổi chu kỳ có lỗ).
   */
  SCHOOL_YEAR: "school_year",
} as const;

export const collectionFrequencySchema = z.enum([
  COLLECTION_FREQUENCY.WEEKLY,
  COLLECTION_FREQUENCY.MONTHLY,
  COLLECTION_FREQUENCY.QUARTERLY,
  COLLECTION_FREQUENCY.YEARLY,
  COLLECTION_FREQUENCY.SCHOOL_YEAR,
]);

/**
 * US-E23.F3-0333-S02 — lịch CŨ không có cột `interval` ⇒ đọc như 1 ⇒ **0 regress**.
 *
 * 🔴 KHÔNG có giá trị enum `biweekly`. "2 tuần/lần" = `{weekly, interval: 2}`. Thêm `biweekly` sẽ
 * tạo HAI cách biểu diễn cùng một thứ ⇒ sớm muộn hai đường tính rồi lệch nhau (§3.8 "Reuse, không
 * cơ chế thứ hai"). Trên sổ tiền, hai công thức cho cùng một lịch thu là lỗi chờ xảy ra.
 */
export const DEFAULT_RECURRENCE_INTERVAL = 1;
export const RECURRENCE_INTERVAL_MAX = 52;
export const INTERVAL_INVALID_MESSAGE = "Số chu kỳ lặp phải là số nguyên từ 1 trở lên";
export const BY_WEEKDAY_ONLY_WEEKLY_MESSAGE =
  "Chỉ chu kỳ theo tuần mới chọn được các thứ trong tuần";
export const BY_WEEKDAY_DUPLICATE_MESSAGE = "Các thứ trong tuần không được trùng nhau";
export const SCHOOL_YEAR_INTERVAL_MESSAGE = "Chu kỳ theo năm học không lặp nhiều năm một lần";

/* ---------------------------------------------------------------------------
 * US-E24.F15-0364-S01 (@UC9616) — PHẠM VI THÀNH VIÊN của lịch định kỳ.
 * QĐ PO 08/08/2026: chọn **`memberIds` = danh sách TRẮNG** (những người ĐƯỢC THU).
 *
 * 🔴 `excludeMemberIds` (danh sách đen) CỐ Ý KHÔNG TỒN TẠI. Hai kiểu phạm vi có hành vi KHÁC NHAU với
 * người vào nhóm sau (trắng: không bị áp · đen: bị áp) ⇒ có cả hai là có hai luật tiền song song, và
 * sớm muộn ai đó cấu hình lệch nhau. `.strict()` biến điều này thành ĐẢM BẢO CỦA KIỂU: gửi
 * `excludeMemberIds` (hoặc gửi CẢ HAI) ⇒ **400 tại biên**, 0 row chạm DB — AC7 không cần một dòng
 * `superRefine` nào.
 *
 * 🔴 Cái giá đã biết và PO chấp nhận có chủ đích: quỹ kiểu *"cả lớp trừ 2 cháu được miễn"* thì cháu
 * chuyển vào tháng 3 **được miễn vĩnh viễn mà không ai nhận ra** — sai tiền IM LẶNG. Bù bằng cách suy
 * diễn thêm member ở worker là **đẻ ra cơ chế phạm vi thứ hai** ⇒ CẤM.
 *
 * Quan hệ với `freeze-member` (@UC9240) — HAI cơ chế, KHÔNG BA (xem @UC9619):
 *  · `memberIds` = **phạm vi áp dụng** của lịch (ai nằm trong diện thu), đặt lúc TẠO, sống trọn đời lịch.
 *  · `freeze-member` = **hoãn/miễn** một người trong khoảng kỳ `[from, to]`, đặt BẤT CỨ LÚC NÀO sau đó.
 *  · Thứ tự: `memberIds` là TẬP NỀN, `freeze` TRỪ TIẾP trên tập đó ("trong phạm vi VÀ không bị freeze
 *    kỳ này"). Người NGOÀI phạm vi mà bị freeze ⇒ vẫn ngoài (freeze là no-op, KHÔNG kéo họ vào).
 * ------------------------------------------------------------------------- */

/**
 * Trần số người trong một lịch — mirror `MAX_ENTRIES` của đường thu MỘT LẦN
 * (`collection-amount-mode.ts`) để hai đường "chỉ định người cụ thể" cùng một giới hạn.
 */
export const SCHEDULE_MEMBER_IDS_MAX = 1000;
export const MEMBER_IDS_EMPTY_MESSAGE = "Vui lòng chọn ít nhất một thành viên áp dụng lịch thu";
export const MEMBER_IDS_DUPLICATE_MESSAGE = "Danh sách thành viên không được trùng nhau";
export const MEMBER_IDS_TOO_MANY_MESSAGE = "Danh sách thành viên vượt quá giới hạn cho phép";

export type CollectionFrequencyContract = z.infer<typeof collectionFrequencySchema>;

/** Trạng thái lịch thu (Pause/Resume — AC5). */
export const SCHEDULE_STATUS_CONTRACT = {
  ACTIVE: "active",
  PAUSED: "paused",
  // US-E16.F3-0184 (@UC9238–@UC9240): cancel = huỷ hẳn (KHÔNG sinh kỳ tương lai; resume ⇒ 409).
  CANCELLED: "cancelled",
} as const;

export const scheduleStatusSchema = z.enum([
  SCHEDULE_STATUS_CONTRACT.ACTIVE,
  SCHEDULE_STATUS_CONTRACT.PAUSED,
  SCHEDULE_STATUS_CONTRACT.CANCELLED,
]);

export type ScheduleStatusContract = z.infer<typeof scheduleStatusSchema>;

/** Giới hạn tên lịch thu (đủ cho tiêu đề đợt; chống over-long). */
export const SCHEDULE_NAME_MAX_LENGTH = 100;

/**
 * US-E24.F36-0450 — trần MÔ TẢ đợt thu, dùng CHUNG cho cả hai đường tạo (lịch định kỳ + adhoc).
 *
 * Bằng `Expense.description` (`expenses.ts:259`): cùng vai trò "người dùng kể khoản này là gì", nên
 * cùng một con số — hai trần khác nhau cho hai ô cùng loại là thứ khiến ai đó phải tra schema mới
 * biết mình được gõ bao nhiêu chữ.
 *
 * 🔴 KHAI Ở ĐÂY, KHÔNG ở `collections.ts`, dù cả hai file cùng dùng. Chiều phụ thuộc đã được chốt là
 * MỘT CHIỀU `collections → collection-schedules` (xem đầu `collections.ts`); khai ở phía kia rồi
 * import ngược về đây tạo **vòng ESM**. `tsc` KHÔNG bắt được vòng đó — nó chỉ nổ lúc chạy, dưới dạng
 * `undefined` rơi vào `.max()` ngay lúc module khởi tạo, tức là mọi request tới hai route này 500.
 */
export const COLLECTION_DESCRIPTION_MAX = 1000;

/** Biên thứ trong tuần (ISO: 1=Thứ Hai .. 7=Chủ Nhật). */
export const WEEKDAY_MIN = 1;
export const WEEKDAY_MAX = 7;

/**
 * Số chữ số tối đa cho amount (SEC MED-1): PG BIGINT max = 9223372036854775807 (19 chữ số). Chuỗi
 * số dài hơn parse BigInt OK nhưng ghi DB ném P2020 → 500. .max(19) chặn ≥20 chữ số, NHƯNG ~8.6% giá
 * trị 19 chữ số (9223372036854775808..9999999999999999999) vẫn vượt BIGINT → cần refine giá trị (dưới).
 */
export const AMOUNT_MAX_DIGITS = 19;

/** Trần PG signed BIGINT (9_223_372_036_854_775_807). amount > trần → 400 (chống overflow P2020→500). */
export const PG_BIGINT_MAX = 9223372036854775807n;

/** Thông điệp lỗi user-facing — một nguồn sự thật api ↔ mobile (khớp AC). */
export const SCHEDULE_NAME_REQUIRED_MESSAGE = "Vui lòng nhập tên đợt thu";
export const AMOUNT_REQUIRED_MESSAGE = "Số tiền phải lớn hơn 0";
export const AMOUNT_INVALID_MESSAGE = "Số tiền phải là số nguyên (đơn vị nhỏ nhất)";
export const START_DATE_INVALID_MESSAGE = "Ngày bắt đầu không hợp lệ (định dạng YYYY-MM-DD)";
export const WEEKDAY_REQUIRED_MESSAGE = "Chu kỳ tuần cần chọn thứ trong tuần (1-7)";

/** Regex 'YYYY-MM-DD' (date-only). Parse → Date UTC midnight ở service. */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** Số nguyên dạng string (cho amount BigInt) — không dấu chấm, không số mũ. */
const INTEGER_STRING_PATTERN = /^\d+$/;

/**
 * Body cho POST /v1/funds/:id/collection-schedules.
 *  - name: trim non-empty ≤100.
 *  - amountPerMemberMinor: STRING số nguyên dương (B1 BigInt — server parse). "0"/âm/float → 400 (AC4).
 *  - currency CỐ Ý VẮNG: kế thừa fund.currency (server đọc — KHÔNG nhận từ body).
 *  - frequency: enum weekly|monthly|quarterly|yearly.
 *  - startDate: 'YYYY-MM-DD'. Quá khứ → 400 (AC2 — kiểm tz ở service/domain, không kiểm ở schema).
 *  - dueDate?: 'YYYY-MM-DD' (hạn đóng — optional).
 *  - weekday?: chỉ dùng khi frequency=weekly (AC3). superRefine: weekly BẮT BUỘC weekday 1..7.
 *  - `.strict()` chặn over-post.
 */
export const createCollectionScheduleRequestSchema = z
  .object({
    name: z.string().trim().min(1, SCHEDULE_NAME_REQUIRED_MESSAGE).max(SCHEDULE_NAME_MAX_LENGTH),
    amountPerMemberMinor: z
      .string()
      .trim()
      .min(1, AMOUNT_REQUIRED_MESSAGE)
      .max(AMOUNT_MAX_DIGITS, AMOUNT_INVALID_MESSAGE)
      .regex(INTEGER_STRING_PATTERN, AMOUNT_INVALID_MESSAGE)
      // SEC MED-1 (residual): .max(19) còn lọt giá trị 19 chữ số vượt BIGINT (vd 9999999999999999999)
      // → ghi DB P2020 → 500. Refine giá trị THẬT ≤ PG_BIGINT_MAX. Bọc try/catch: chuỗi không-số (vd
      // "50000.5" đã rớt .regex nhưng refine vẫn chạy) → BigInt() ném ⇒ trả false (invalid), KHÔNG throw.
      .refine((v) => {
        try {
          return BigInt(v) <= PG_BIGINT_MAX;
        } catch {
          return false;
        }
      }, AMOUNT_INVALID_MESSAGE),
    frequency: collectionFrequencySchema,
    startDate: z.string().trim().regex(ISO_DATE_PATTERN, START_DATE_INVALID_MESSAGE),
    dueDate: z.string().trim().regex(ISO_DATE_PATTERN, START_DATE_INVALID_MESSAGE).optional(),
    weekday: z.number().int().min(WEEKDAY_MIN).max(WEEKDAY_MAX).optional(),
    /** Lặp mỗi N đơn vị. Vắng ⇒ 1 (tương thích ngược body cũ). */
    interval: z
      .number()
      .int(INTERVAL_INVALID_MESSAGE)
      .min(DEFAULT_RECURRENCE_INTERVAL, INTERVAL_INVALID_MESSAGE)
      .max(RECURRENCE_INTERVAL_MAX, INTERVAL_INVALID_MESSAGE)
      .default(DEFAULT_RECURRENCE_INTERVAL),
    /** CHỈ dùng với `weekly`: các thứ sinh mốc (ISO 1=Thứ Hai..7=Chủ Nhật). */
    byWeekday: z
      .array(z.number().int().min(WEEKDAY_MIN).max(WEEKDAY_MAX))
      .min(1)
      .max(7)
      .optional(),
    /**
     * @UC9616 — phạm vi thành viên (danh sách TRẮNG `groupMemberId`). VẮNG ⇒ mọi member `active` như
     * trước (0 REGRESS). `[]` ⇒ 400: lịch thu 0 người là cấu hình vô nghĩa, im lặng tạo ra nó = một
     * lịch không bao giờ thu được gì mà người dùng tưởng đã xong.
     */
    memberIds: z
      .array(z.string().uuid())
      .min(1, MEMBER_IDS_EMPTY_MESSAGE)
      .max(SCHEDULE_MEMBER_IDS_MAX, MEMBER_IDS_TOO_MANY_MESSAGE)
      .optional(),
    /**
     * US-E24.F36-0450 (@UC0050) — MÔ TẢ khoản thu, lưu trên LỊCH và chép xuống từng kỳ lúc sinh.
     *
     * 🔴 Lưu trên lịch chứ không chỉ trên đợt đầu: worker sinh kỳ 2,3,4… nhiều tháng sau khi request
     * này kết thúc. Không lưu ⇒ mọi kỳ từ tháng thứ hai mất mô tả, im lặng. Cùng lý lẽ đã ghi cho
     * `memberIds` ngay trên.
     *
     * OPTIONAL + trần dùng chung `COLLECTION_DESCRIPTION_MAX` với đường adhoc — hai route anh em
     * trên cùng một khái niệm, hai trần khác nhau là chỗ để client gửi hợp lệ ở đường này mà 400 ở
     * đường kia.
     */
    description: z.string().trim().max(COLLECTION_DESCRIPTION_MAX).optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    const isWeekly = body.frequency === COLLECTION_FREQUENCY.WEEKLY;

    // weekly phải chọn thứ — qua `weekday` (đường cũ) HOẶC `byWeekday` (đường mới). Không đòi cả hai.
    if (isWeekly && body.weekday === undefined && body.byWeekday === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["weekday"],
        message: WEEKDAY_REQUIRED_MESSAGE,
      });
    }

    // 🔴 byWeekday với tần suất khác weekly ⇒ CHẶN, không im lặng bỏ qua: người dùng chọn "thứ Tư
    // hằng tháng" rồi nhận lịch khác thứ họ chọn là nói dối về nghĩa vụ đóng tiền.
    if (!isWeekly && body.byWeekday !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["byWeekday"],
        message: BY_WEEKDAY_ONLY_WEEKLY_MESSAGE,
      });
    }

    // Trùng thứ ⇒ CHẶN, không tự dedupe: cấu hình mơ hồ phải nổ ở người nhập, không nổ ở sổ cái.
    if (body.byWeekday && new Set(body.byWeekday).size !== body.byWeekday.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["byWeekday"],
        message: BY_WEEKDAY_DUPLICATE_MESSAGE,
      });
    }

    // @UC9616 — trùng `groupMemberId` ⇒ CHẶN, KHÔNG tự dedupe. Cùng lý lẽ `byWeekday`: cấu hình mơ hồ
    // phải nổ ở người nhập, không nổ ở sổ cái. Tự dedupe nghĩa là im lặng sửa ý người dùng trên một
    // danh sách quyết định AI PHẢI ĐÓNG TIỀN.
    // 🔴 So TRÙNG phải KHÔNG phân biệt hoa/thường. `z.string().uuid()` khớp case-insensitive ⇒
    // `["7C9E…", "7c9e…"]` (CÙNG một người) lọt qua phép so chuỗi thô. Service chuẩn hoá lowercase
    // rồi mới kiểm sở hữu, nên `memberIds.length = 2` mà `groupMember.count` = 1 ⇒ trả **404**
    // "có id không thuộc nhóm" — vừa SAI mã lỗi so với 400 đã khai ở contract/UC/docs, vừa báo sai
    // nguyên nhân: người dùng đi tìm một thành viên lạ không hề tồn tại.
    // Vá tại BIÊN (không hạ `.toLowerCase()` của service xuống đây): service là cửa vào của cả
    // đường không-qua-zod, bỏ chuẩn hoá ở đó sẽ mở lại HIGH-1.
    if (
      body.memberIds &&
      new Set(body.memberIds.map((id) => id.toLowerCase())).size !== body.memberIds.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["memberIds"],
        message: MEMBER_IDS_DUPLICATE_MESSAGE,
      });
    }

    // Năm học lặp "2 năm một lần" không có nghĩa nghiệp vụ ⇒ chặn ở biên.
    if (
      body.frequency === COLLECTION_FREQUENCY.SCHOOL_YEAR &&
      body.interval !== DEFAULT_RECURRENCE_INTERVAL
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["interval"],
        message: SCHOOL_YEAR_INTERVAL_MESSAGE,
      });
    }
  });

export type CreateCollectionScheduleRequest = z.infer<
  typeof createCollectionScheduleRequestSchema
>;

/**
 * Shape response cho POST collection-schedules (201) + pause/resume (200).
 *  - schedule: lịch vừa tạo/đổi trạng thái (id/name/frequency/scheduleStatus/amount-as-string/currency).
 *  - collection: đợt đầu sinh đồng bộ (id/periodKey/status) — null khi không sinh (vd paused).
 *  - duesCreated: số member_dues sinh ở đợt đầu — member ACTIVE **trong phạm vi lịch** (@UC9616
 *    `memberIds`, danh sách TRẮNG). Không truyền `memberIds` ⇒ RỖNG = không giới hạn ⇒ mọi member
 *    ACTIVE, đúng hành vi trước US-E24.F15-0364.
 *  - amountPerMemberMinor trả dạng STRING (B1 — KHÔNG number để tránh mất chính xác). `.strict()`.
 */
export const collectionScheduleResponseSchema = z
  .object({
    schedule: z
      .object({
        id: z.string().uuid(),
        fundId: z.string().uuid(),
        name: z.string(),
        amountPerMemberMinor: z.string().regex(INTEGER_STRING_PATTERN),
        currency: z.string().length(3),
        frequency: collectionFrequencySchema,
        scheduleStatus: scheduleStatusSchema,
      })
      .strict(),
    collection: z
      .object({
        id: z.string().uuid(),
        periodKey: z.string(),
        status: z.string(),
      })
      .strict()
      .nullable(),
    duesCreated: z.number().int().nonnegative(),
  })
  .strict();

export type CollectionScheduleResponse = z.infer<typeof collectionScheduleResponseSchema>;

/* ===========================================================================
 * GET /v1/funds/:id/collection-schedules — List + Detail (read-slice)
 * (US-E6.F1-0036 / @UC0038, Phase 7 Collections — @gate rbac, read-only).
 *
 * Cross-check index §3 (PO chốt 30/06): đọc lịch thu của quỹ. RBAC REUSE FundAccessGuard +
 * @RequireFundRoles(OWNER, ADMIN, TREASURER) — Member → 403; non-member/cross-tenant/non-UUID → 404.
 * Read-only: KHÔNG ghi ledger, KHÔNG mutate member_dues.
 *
 * INVARIANTS:
 *  - B1 tiền BigInt → STRING (KHÔNG float/number) ở mọi field amount.
 *  - `nextDate` server tính (domain computeNextDate) — client KHÔNG tính lại (không drift).
 *  - List bọc envelope `{ schedules: [...] }` (ApiClient.getJson trả Map, không nhận array trần).
 * ========================================================================= */

/** Regex 'YYYY-MM-DD' cho nextDate/dueDate trên response (date-only, server format). */
const ISO_DATE_RESPONSE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** recurrence JSON lưu ở schedule ({ freq, weekday? }) — passthrough cho client render nhãn. */
export const scheduleRecurrenceSchema = z
  .object({
    freq: collectionFrequencySchema,
    weekday: z.number().int().min(WEEKDAY_MIN).max(WEEKDAY_MAX).optional(),
    /**
     * 🔴 US-E23.F3-0333-S04 (@UC9530) — PHẢI có ở đường ĐỌC. Không khai ở đây thì client đặt
     * "2 tuần/lần" rồi đọc lại thấy "hằng tuần": UI nói khác DB về NGHĨA VỤ ĐÓNG TIỀN.
     * Bắt buộc (không optional) — server LUÔN trả, lịch cũ thiếu khoá trong Json thì service điền 1.
     */
    interval: z.number().int().min(DEFAULT_RECURRENCE_INTERVAL).max(RECURRENCE_INTERVAL_MAX),
    byWeekday: z
      .array(z.number().int().min(WEEKDAY_MIN).max(WEEKDAY_MAX))
      .min(1)
      .max(7)
      .optional(),
  })
  .nullable();

/**
 * 1 item lịch thu trong list (AC1). amount STRING (B1). nextDate = ngày đợt kế tiếp (YYYY-MM-DD)
 * hoặc null nếu không xác định. `.strict()` chống dư field.
 */
export const collectionScheduleListItemSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    frequency: collectionFrequencySchema,
    recurrence: scheduleRecurrenceSchema,
    amountPerMemberMinor: z.string().regex(INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    scheduleStatus: scheduleStatusSchema,
    nextDate: z.string().regex(ISO_DATE_RESPONSE_PATTERN).nullable(),
  })
  .strict();

export type CollectionScheduleListItem = z.infer<typeof collectionScheduleListItemSchema>;

/** Response GET list — envelope `{ schedules: [...] }`. Quỹ rỗng → `{ schedules: [] }`. */
export const listCollectionSchedulesResponseSchema = z
  .object({
    schedules: z.array(collectionScheduleListItemSchema),
  })
  .strict();

export type ListCollectionSchedulesResponse = z.infer<
  typeof listCollectionSchedulesResponseSchema
>;

/** 1 đợt thu đã sinh (history detail) — AC2. amount KHÔNG cần ở history (đã ở schedule). */
export const collectionHistoryItemSchema = z
  .object({
    id: z.string().uuid(),
    periodKey: z.string(),
    dueDate: z.string().regex(ISO_DATE_RESPONSE_PATTERN).nullable(),
    status: z.string(),
    createdAt: z.string(),
    /**
     * US-E23.F12-0341-S02 (@UC9532 · QĐ PO 01/08): nhãn học kỳ 'HK1'/'HK2' — **TRƯỜNG DẪN XUẤT**,
     * server tính từ `periodKey` + config năm học, KHÔNG có cột DB nào lưu nó.
     *
     * `null` ở ba ca, và cả ba đều là "không biết" chứ không phải "không có":
     *  · lịch không phải tần suất năm học · kỳ rơi ngoài cửa sổ năm học · config học kỳ không mạch lạc.
     * 🔴 Client KHÔNG được tự suy nhãn từ tháng: luật học kỳ là config 2 mức của từng nhóm, suy ở
     * client là dựng luật thứ hai và nó sẽ lệch ngay lần đầu ai đó đổi mốc.
     */
    semesterLabel: z.string().nullable(),
  })
  .strict();

export type CollectionHistoryItem = z.infer<typeof collectionHistoryItemSchema>;

/**
 * Response GET detail (AC2) — schedule (list-item shape) + history các Collection nó sinh
 * (order createdAt desc). `.strict()`.
 */
/**
 * 🔴 US-E24.F25-0380-S01 (@UC9662) — PHẠM VI của lịch: *"ai sẽ bị thu"*.
 *
 * `0364` cho phép tạo lịch với danh sách TRẮNG (`memberIds`) nhưng **không có đường đọc lại**: chọn
 * nhầm một người thì phải xoá cả lịch, tạo lại. Ghép với luật đã chốt (*người vào nhóm SAU không bị
 * áp*), đó là chỗ **sai tiền im lặng** dễ xảy ra nhất — nên phần này là điều kiện tiên quyết để bật
 * màn "Định kỳ + chọn người".
 *
 * `mode='all'` ⇒ áp cho MỌI member active (lịch không đặt danh sách — hành vi cũ). `mode='subset'` ⇒
 * chỉ những người trong `memberIds`. Trả kèm `outOfScopeMemberIds` để UI dựng được ô chọn mà không
 * phải ghép hai nguồn.
 */
export const collectionScheduleScopeSchema = z
  .object({
    mode: z.enum(["all", "subset"]),
    memberIds: z.array(z.string().uuid()),
    outOfScopeMemberIds: z.array(z.string().uuid()),
  })
  .strict();
export type CollectionScheduleScope = z.infer<typeof collectionScheduleScopeSchema>;

/** Body PATCH phạm vi. Rỗng ⇒ 422 (fail-loud) chứ KHÔNG âm thầm quay về "áp cả nhóm". */
export const patchCollectionScheduleScopeSchema = z
  .object({ memberIds: z.array(z.string().uuid()).min(1) })
  .strict();
export type PatchCollectionScheduleScope = z.infer<
  typeof patchCollectionScheduleScopeSchema
>;

export const SCHEDULE_SCOPE_CODE = {
  /** Phạm vi rỗng ⇒ 422. Không fallback "cả nhóm" — im lặng mở rộng nghĩa vụ tiền là điều cấm. */
  EMPTY_SCOPE: "COLLECTION_SCHEDULE_SCOPE_EMPTY",
} as const;
export const SCHEDULE_SCOPE_EMPTY_MESSAGE =
  "Danh sách người áp dụng không được để trống. Nếu muốn thu cả nhóm, hãy chọn tất cả thành viên.";

export const collectionScheduleDetailResponseSchema = z
  .object({
    schedule: collectionScheduleListItemSchema,
    history: z.array(collectionHistoryItemSchema),
    /** 🔴 @UC9662: ai đang trong phạm vi thu của lịch này. */
    scope: collectionScheduleScopeSchema,
  })
  .strict();

export type CollectionScheduleDetailResponse = z.infer<
  typeof collectionScheduleDetailResponseSchema
>;

/* ===========================================================================
 * US-E16.F3-0184-recurring-advanced — reprice / skip / cancel / member-freeze
 * (@UC9238–@UC9240). Body DTO cho endpoint mutate schedule. RBAC treasurer/owner
 * (guard ở controller). Tiền = STRING minor (B1 — server parse BigInt).
 * ========================================================================= */

/** periodKey ordinal — 'YYYY-MM' | 'YYYY-Qn' | 'YYYY-Www' | 'YYYY' (khớp derivePeriodKey domain). */
export const PERIOD_KEY_PATTERN = /^\d{4}(-(0[1-9]|1[0-2]|Q[1-4]|W\d{2}))?$/;
export const PERIOD_KEY_INVALID_MESSAGE = "Kỳ không hợp lệ (vd 2026-07 / 2026-Q3 / 2026-W27 / 2026)";
export const REPRICE_PAST_MESSAGE = "Không thể đổi giá cho kỳ đã sinh (không hồi tố)";

/**
 * @UC9238 reprice: PATCH .../reprice { effectiveFromPeriodKey, amountMinor }. Mốc quá khứ (kỳ đã sinh)
 * ⇒ 422 ở service. amountMinor STRING số nguyên dương (B1). `.strict()`.
 */
export const repriceScheduleRequestSchema = z
  .object({
    effectiveFromPeriodKey: z.string().trim().regex(PERIOD_KEY_PATTERN, PERIOD_KEY_INVALID_MESSAGE),
    amountMinor: z
      .string()
      .trim()
      .min(1, AMOUNT_REQUIRED_MESSAGE)
      .max(AMOUNT_MAX_DIGITS, AMOUNT_INVALID_MESSAGE)
      .regex(INTEGER_STRING_PATTERN, AMOUNT_INVALID_MESSAGE)
      .refine((v) => BigInt(v) > 0n, AMOUNT_REQUIRED_MESSAGE)
      .refine((v) => BigInt(v) <= PG_BIGINT_MAX, AMOUNT_INVALID_MESSAGE),
  })
  .strict();

export type RepriceScheduleRequest = z.infer<typeof repriceScheduleRequestSchema>;

/** @UC9239 skip: POST .../skip { periodKey }. Idempotent (2 lần → 1 entry). `.strict()`. */
export const skipPeriodRequestSchema = z
  .object({
    periodKey: z.string().trim().regex(PERIOD_KEY_PATTERN, PERIOD_KEY_INVALID_MESSAGE),
  })
  .strict();

export type SkipPeriodRequest = z.infer<typeof skipPeriodRequestSchema>;

/**
 * @UC9240 member-freeze: POST .../freeze-member { groupMemberId, fromPeriodKey, toPeriodKey? }.
 * toPeriodKey NULL/absent = freeze mở. to < from ⇒ 400. `.strict()`.
 */
export const freezeMemberRequestSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    fromPeriodKey: z.string().trim().regex(PERIOD_KEY_PATTERN, PERIOD_KEY_INVALID_MESSAGE),
    toPeriodKey: z
      .string()
      .trim()
      .regex(PERIOD_KEY_PATTERN, PERIOD_KEY_INVALID_MESSAGE)
      .nullable()
      .optional(),
  })
  .strict()
  .refine(
    (v) => v.toPeriodKey === null || v.toPeriodKey === undefined || v.toPeriodKey >= v.fromPeriodKey,
    { message: "Kỳ kết thúc phải >= kỳ bắt đầu", path: ["toPeriodKey"] },
  );

export type FreezeMemberRequest = z.infer<typeof freezeMemberRequestSchema>;
